import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { environment } from 'environments/environment';
import { UserService } from 'app/core/user/user.service';
import { StorageService } from 'ng-blockchainx';

interface LoginResponse {
    data: {
        user?: any;
        session: {
            session_token: string;
        }
    }
}

interface NonceResponse {
    data: {
        nonce_token: string;
        message: string;
    }
}

@Injectable()
export class AuthService {
    public _authenticated: boolean = false;

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private storageService: StorageService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: any) {
        this.storageService.set('accessToken', token);
    }

    get accessToken(): any {
        return  this.storageService.get('accessToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    signIn(walletAddress: any, connection: any, isMetaMaskConnected: any): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        // Emits only once POST /login has answered and the session is stored: the sign-in
        // page navigates on this emission, and AuthGuard rejects the next route without it.
        return this._httpClient.get(`${environment.API_BASE_URL}/check-admin/${walletAddress.wallet_address}`).pipe(
            switchMap((response: any) => {
                if (response?.status_code != 200) {
                    return throwError(response?.message || 'Admin not found');
                }
                return this.signedLogin(walletAddress.wallet_address).pipe(
                    map((res: LoginResponse) => {
                        this.accessToken = res.data.session.session_token;
                        this.storageService.set('connection', connection);
                        this.storageService.set('account', { walletAddress: connection.data[0], chainId: connection.chainId });
                        this.storageService.set('isWalletConnected', isMetaMaskConnected);
                        localStorage.setItem('id', res.data.user._id);

                        this._authenticated = true;
                        return response;
                    })
                );
            }),
            catchError((err: any) => throwError(signInErrorMessage(err)))
        );
    }

    checkAdmin(walletAddress: any): Observable<any> {
        return this._httpClient.get(`${environment.API_BASE_URL}/check-admin/${walletAddress.wallet_address}`).pipe(
            switchMap((response: any) => {
                if (response.status_code == 200) {
                    this.signedLogin(walletAddress.wallet_address).subscribe(
                        (res: LoginResponse) => {
                            this.accessToken = res.data.session.session_token;
                        },
                        (err: any) => console.error('Admin re-login after account switch failed', err)
                    );
                }
                return of(response);
            }), catchError((err) => {
                return throwError(err.error.message)
            })
        )

    }

    /**
     * Wallet-signature login: GET /nonce, sign the returned message in MetaMask, then POST /login
     * with the signature. A nonce is single-use, so every attempt fetches a fresh one.
     */
    private signedLogin(walletAddress: string): Observable<LoginResponse> {
        const params = new HttpParams().set('wallet_address', walletAddress);
        return this._httpClient.get<NonceResponse>(`${environment.API_BASE_URL}/nonce`, { params }).pipe(
            switchMap((res: NonceResponse) => {
                const nonceToken = res?.data?.nonce_token;
                const message = res?.data?.message;
                if (!nonceToken || !message) {
                    return throwError('Invalid nonce response from server');
                }
                return from(this.signLoginMessage(walletAddress, message)).pipe(
                    switchMap((signature: string) => this._httpClient.post<LoginResponse>(`${environment.API_BASE_URL}/login`, {
                        wallet_address: walletAddress,
                        nonce_token: nonceToken,
                        signature
                    }))
                );
            })
        );
    }

    /**
     * Asks MetaMask to sign the login message. The message is sent hex-encoded so the wallet signs
     * its exact UTF-8 bytes, which is what the backend recovers the signer from.
     */
    protected signLoginMessage(walletAddress: string, message: string): Promise<string> {
        const ethereum = window['ethereum'];
        if (!ethereum?.request) {
            return Promise.reject('MetaMask is not available');
        }
        const hexMessage = '0x' + Array.from(new TextEncoder().encode(message))
            .map((byte: number) => byte.toString(16).padStart(2, '0'))
            .join('');
        return ethereum.request({ method: 'personal_sign', params: [hexMessage, walletAddress] });
    }

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post('api/auth/forgot-password', email);
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    // signIn(credentials: { email: string; password: string }): Observable<any>
    // {
    //     // Throw error, if the user is already logged in
    //     if ( this._authenticated )
    //     {
    //         return throwError('User is already logged in.');
    //     }

    //     return this._httpClient.post('api/auth/sign-in', credentials).pipe(
    //         switchMap((response: any) => {

    //             // Store the access token in the local storage
    //             this.accessToken = response.accessToken;

    //             // Set the authenticated flag to true
    //             this._authenticated = true;

    //             // Store the user on the user service
    //             this._userService.user = response.user;

    //             // Return a new observable with the response
    //             return of(response);
    //         })
    //     );
    // }

    /**
     * Sign in using the access token
     */
    signInUsingToken(): Observable<any> {
        // Renew token
        return this._httpClient.post('api/auth/refresh-access-token', {
            accessToken: this.accessToken
        }).pipe(
            catchError((err) =>

                // Return false
                of(err)
            ),
            switchMap((response: any) => {
                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                this._userService.user = response.user;

                // Return true
                return of(true);
            })
        );
    }

    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        this.storageService.remove('accessToken');
        this.storageService.remove('connection');
        this.storageService.remove('account');
        this.storageService.remove('isWalletConnected');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Sign up
     *
     * @param user
     */
    signUp(user: { name: string; email: string; password: string; company: string }): Observable<any> {
        return this._httpClient.post('api/auth/sign-up', user);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        // if ( this._authenticated )
        // {            
        //     return of(true);
        // }

        // Check the access token availability
        if (this.accessToken && this.accessToken != undefined && this.accessToken != '' && this.storageService.get('isWalletConnected') == true && this.storageService.get('connection')  && this.storageService.get('account')) {
            this._authenticated = true;
            return of(true);
        }
        else {
            return of(false);
        }

        // Check the access token expire date
        // if ( AuthUtils.isTokenExpired(this.accessToken) )
        // {
        //     return of(false);
        // }

        // If the access token exists and it didn't expire, sign in using it
        // return this.signInUsingToken();
    }
}

/**
 * A network error or a non-JSON response has no `error.message`, so reading it unguarded
 * would throw inside catchError and the sign-in page would show nothing.
 */
function signInErrorMessage(err: any): string {
    if (typeof err === 'string') {
        return err;
    }
    // EIP-1193 user-rejected request: the admin dismissed the MetaMask signature prompt.
    if (err?.code === 4001) {
        return 'Signature request was rejected in MetaMask';
    }
    return err?.error?.message || 'Sign-in failed, please try again';
}
